import { getAccessToken } from './auth.js';
import type {
  ArticleDetailResponse,
  ChannelMapping,
  DashboardSalesResponse,
  DashboardStockResponse,
  MeResponse,
  PortalUser,
  ScopeOption,
  ScopeRule,
  ScopeType,
  ViewerGroup,
} from './types.js';

const apiBase = import.meta.env.VITE_MYEP_API_BASE ?? '/api/my-eperformances';

export async function apiGet<T>(path: string): Promise<T> {
  return request<T>(path, { method: 'GET' });
}

export async function apiPost<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, { method: 'POST', body: JSON.stringify(body) });
}

export async function apiPatch<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
}

export async function getMe(): Promise<MeResponse> {
  return apiGet<MeResponse>('/me');
}

export async function getAdminStatus(): Promise<{ keycloakProvisioningEnabled: boolean }> {
  return apiGet('/admin/auth-status');
}

export async function listViewerGroups(): Promise<{ viewerGroups: ViewerGroup[] }> {
  return apiGet('/admin/viewer-groups?includeInactive=true&limit=500');
}

export async function listPortalViewerGroups(): Promise<{ viewerGroups: ViewerGroup[] }> {
  return apiGet('/viewer-groups');
}

export async function saveViewerGroup(input: ViewerGroupDraft): Promise<{ viewerGroup: ViewerGroup }> {
  const body = {
    code: input.code,
    name: input.name,
    description: input.description || null,
    logoUrl: input.logoUrl || null,
    showRevenue: input.showRevenue,
    active: input.active,
    settings: { locale: input.locale },
    scopeRules: input.scopeRules.map((rule) => ({
      scopeType: rule.scope_type,
      scopeValue: rule.scope_value,
      includeDescendants: rule.include_descendants,
      active: rule.active,
    })),
  };
  if (input.id) return apiPatch(`/admin/viewer-groups/${input.id}`, body);
  return apiPost('/admin/viewer-groups', body);
}

export async function listUsers(): Promise<{ users: PortalUser[] }> {
  return apiGet('/admin/users?includeDisabled=true&limit=500');
}

export async function createUser(input: UserDraft): Promise<{ user: PortalUser }> {
  return apiPost('/admin/users', input);
}

export async function updateUser(id: string, input: Partial<UserDraft>): Promise<{ user: PortalUser }> {
  return apiPatch(`/admin/users/${id}`, input);
}

export async function searchScopeOptions(
  type: ScopeType,
  q: string,
): Promise<{ options: ScopeOption[] }> {
  const params = new URLSearchParams({ type, limit: '80' });
  if (q.trim()) params.set('q', q.trim());
  return apiGet(`/admin/scope-options?${params.toString()}`);
}

export async function listChannelMappings(): Promise<{ channelMappings: ChannelMapping[] }> {
  return apiGet('/admin/channel-mappings?includeInactive=true&limit=1000');
}

export async function saveChannelMapping(input: ChannelMappingDraft): Promise<{ channelMapping: ChannelMapping }> {
  const body = {
    rawChannel: input.rawChannel,
    groupName: input.groupName,
    active: input.active,
    excludeFromDashboard: input.excludeFromDashboard,
  };
  if (input.id) return apiPatch(`/admin/channel-mappings/${input.id}`, body);
  return apiPost('/admin/channel-mappings', body);
}

export interface DashboardQuery {
  viewerGroupId?: string | null;
  from: string;
  to: string;
  granularity?: 'day' | 'week' | 'month';
  articleId?: string | null;
  articleIds?: string[];
  channels?: string[];
  countries?: string[];
  periodFrom?: string | null | undefined;
  periodTo?: string | null | undefined;
  q?: string;
  limit?: number;
}

export async function getDashboardSales(query: DashboardQuery): Promise<DashboardSalesResponse> {
  const params = dashboardParams(query);
  return apiGet(`/dashboard/sales?${params.toString()}`);
}

export async function getDashboardStock(query: Pick<DashboardQuery, 'viewerGroupId' | 'q' | 'limit'>): Promise<DashboardStockResponse> {
  const params = new URLSearchParams();
  if (query.viewerGroupId) params.set('viewerGroupId', query.viewerGroupId);
  if (query.q?.trim()) params.set('q', query.q.trim());
  if (query.limit) params.set('limit', String(query.limit));
  return apiGet(`/dashboard/stock?${params.toString()}`);
}

export async function getArticleDetail(articleId: string, query: DashboardQuery): Promise<ArticleDetailResponse> {
  const params = dashboardParams(query);
  return apiGet(`/dashboard/articles/${encodeURIComponent(articleId)}?${params.toString()}`);
}

async function request<T>(path: string, init: RequestInit): Promise<T> {
  const token = await getAccessToken();
  if (!token) throw new Error('Nicht angemeldet.');
  const response = await fetch(`${apiBase}${path}`, {
    ...init,
    headers: {
      accept: 'application/json',
      'content-type': 'application/json',
      authorization: `Bearer ${token}`,
      ...init.headers,
    },
  });
  if (!response.ok) {
    let message = `API Fehler ${response.status}`;
    try {
      const payload = (await response.json()) as { error?: string };
      if (payload.error) message = payload.error;
    } catch {
      // Keep HTTP fallback.
    }
    throw new Error(message);
  }
  return (await response.json()) as T;
}

function dashboardParams(query: DashboardQuery): URLSearchParams {
  const params = new URLSearchParams({
    from: query.from,
    to: query.to,
    granularity: query.granularity ?? 'day',
  });
  if (query.viewerGroupId) params.set('viewerGroupId', query.viewerGroupId);
  if (query.articleId) params.set('articleId', query.articleId);
  if (query.articleIds?.length) params.set('articleIds', query.articleIds.join(','));
  if (query.channels?.length) params.set('channels', query.channels.join(','));
  if (query.countries?.length) params.set('countries', query.countries.join(','));
  if (query.periodFrom) params.set('periodFrom', query.periodFrom);
  if (query.periodTo) params.set('periodTo', query.periodTo);
  if (query.q?.trim()) params.set('q', query.q.trim());
  if (query.limit) params.set('limit', String(query.limit));
  return params;
}

export interface ViewerGroupDraft {
  id?: string;
  code: string;
  name: string;
  description: string;
  logoUrl: string;
  showRevenue: boolean;
  active: boolean;
  locale: 'de-DE' | 'en-US';
  scopeRules: ScopeRule[];
}

export interface UserDraft {
  email: string;
  displayName?: string | null;
  viewerGroupId?: string | null;
  role: 'viewer' | 'admin';
  disabled?: boolean;
  oidcSubject?: string | null;
  password?: string | null;
}

export interface ChannelMappingDraft {
  id?: string | null;
  rawChannel: string;
  groupName: string;
  active: boolean;
  excludeFromDashboard: boolean;
}
